import { toArr } from '@rehaje/shared'

import type { TypedDefinition } from '../models'
import type { SchemaFunctionSpecific, TypedSchema } from '../types'
import type { TRecord } from '@rehaje/shared'

interface JSDocTemplate {
  async?: boolean
  deprecated?: boolean
  description?: string
  example?: string
  generator?: boolean
  param?: string[]
  returns?: string
  title?: string
}

const templateOrder = [
  'title',
  'description',
  'deprecated',
  'example',
  'async',
  'generator',
  'param',
  'returns'
] as const

export function createJSDoc(
  schema:
    | (Omit<TypedSchema, 'async' | 'generator' | 'parameters' | 'return'> &
        Pick<
          SchemaFunctionSpecific,
          'async' | 'generator' | 'parameters' | 'return'
        >)
    | (TRecord & TypedDefinition),
  title?: string,
  description?: string
): string {
  const template: JSDocTemplate = {
    async: schema.async as boolean | undefined,
    deprecated: schema.deprecated,
    description: description ?? schema.description,
    example: schema.example,
    generator: schema.generator as boolean | undefined,
    param: schema.parameters
      ? toArr(schema.parameters).map((param) => {
          return `${param.name ?? ''} ${param.title ?? ''} ${param.description ?? ''}`
        })
      : undefined,
    returns: schema.return
      ? `${schema.return.title ?? ''} ${schema.return.description ?? ''}`
      : undefined,
    title: title ?? schema.title
  }

  return createJSDocTemplate(template)
}

function createJSDocTemplate(template: JSDocTemplate): string {
  const templates: string[] = []
  for (const key of templateOrder) {
    const items = toArr(template[key])
      .filter((item) => (typeof item === 'string' ? item.trim() : item))
      .map((item) => {
        if (key === 'title') return String(item)
        if (typeof item === 'boolean') return `@${key}`
        return `@${key} ${item.trim().replaceAll('\n', '\n * ')}`
      })
    if (items.length > 0) templates.push(...items)
  }
  return templates.length > 0
    ? `\n/**\n * ${templates.join('\n * ')}\n */\n`
    : ''
}
